import { faArrowDownToLine, faXmark } from '@fortawesome/pro-regular-svg-icons';
import * as Dialog from '@radix-ui/react-dialog';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { AttachmentIcon } from './toolDisplay.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import classnames from '../../core/utils/classnames.tsx';

const DownloadIcon = withIcon(faArrowDownToLine);
const CloseIcon = withIcon(faXmark);

export interface ChatAttachmentData {
    id: string;
    source?: string;
    fileName: string;
    contentType?: string | null;
    downloadUrl?: string | null;
    previewUrl?: string | null;
}

export const isPreviewable = (contentType?: string | null) =>
    Boolean(contentType && (contentType === 'application/pdf' || contentType.startsWith('image/')));

export const AttachmentChip = ({
    attachment,
    onClick,
    className = '',
}: {
    attachment: ChatAttachmentData;
    onClick: () => void;
    className?: string;
}) => (
    <button
        type="button"
        onClick={onClick}
        className={classnames(
            'flex cursor-pointer items-center gap-1.5 rounded-full border-none px-2.5 py-1 text-xs font-medium',
            className
        )}
    >
        <AttachmentIcon />
        {attachment.fileName}
    </button>
);

export const AttachmentPreviewModal = ({
    attachment,
    onClose,
}: {
    attachment: ChatAttachmentData | null;
    onClose: () => void;
}) => {
    const { t } = useTranslation('chat');
    const previewable = isPreviewable(attachment?.contentType);
    const isImage = Boolean(attachment?.contentType?.startsWith('image/'));

    return (
        <Dialog.Root open={Boolean(attachment)} onOpenChange={(open) => !open && onClose()}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[1000000] flex h-full w-full flex-col bg-surface-black/80">
                    <Dialog.Content className="flex h-full w-full flex-col">
                        <div className="flex w-full items-center justify-between bg-surface-black px-6 py-4 text-content-white">
                            <Dialog.Title className="text-base font-medium">{attachment?.fileName}</Dialog.Title>
                            <div className="flex items-center gap-1">
                                {attachment?.downloadUrl && (
                                    <a
                                        href={attachment.downloadUrl}
                                        download={attachment.fileName}
                                        className="flex h-8 w-8 items-center justify-center rounded transition-colors hover:bg-surface-white/20"
                                    >
                                        <DownloadIcon />
                                    </a>
                                )}
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex h-8 w-8 items-center justify-center rounded border-none bg-transparent text-content-white transition-colors hover:bg-surface-white/20"
                                >
                                    <CloseIcon />
                                </button>
                            </div>
                        </div>
                        <div className="flex min-h-0 flex-1 items-center justify-center px-8 py-6">
                            {previewable && attachment?.previewUrl ? (
                                isImage ? (
                                    <img
                                        src={attachment.previewUrl}
                                        alt={attachment.fileName}
                                        className="max-h-full max-w-full object-contain"
                                    />
                                ) : (
                                    <object
                                        data={attachment.previewUrl}
                                        type="application/pdf"
                                        className="h-full w-full rounded-xl bg-surface-white"
                                    >
                                        <iframe src={attachment.previewUrl} className="h-full w-full" />
                                    </object>
                                )
                            ) : (
                                <div className="flex flex-col items-center gap-2 rounded-lg bg-surface-black px-12 py-8 text-content-white">
                                    <div className="text-sm font-semibold">{t('thread.previewUnavailableTitle')}</div>
                                    <div className="text-sm text-content-medium">
                                        {t('thread.previewUnavailableDescription')}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
